import {Axios} from "axios";
import {PageableDto} from "../pagination/PagingFilterDto";
import {SortableDto} from "../pagination/SortingFilterDto";
import {PageableResultListDto} from "../pagination/PageableResultListDto";

export enum PersonDataOrigin {
    NIA = "NIA",
    REZA = "REZA",
    ROB = "ROB"
}

export enum RobSubscriptionStatus {
    SUBSCRIBED_TO_UPDATES = "SUBSCRIBED_TO_UPDATES",
    SUBSCRIBED_UPDATE_IN_NEXT_FORM_SESSION = "SUBSCRIBED_UPDATE_IN_NEXT_FORM_SESSION",
    UNSUBSCRIBED_FROM_UPDATES = "UNSUBSCRIBED_FROM_UPDATES",
}

export interface PersonAclDto {
    canForceSyncWithRob: boolean;
    canSubscribeToUpdatesFromRob: boolean;
    canUnsubscribeToUpdatesFromRob: boolean;
    canForceSyncRezaRepresentations: boolean;
}

export interface PersonDto {
    id: number;
    firstName?: string;
    lastName?: string;
    email?: string;
    phone?: string;
    databox?: string;

    dataOrigin?: PersonDataOrigin;
    subscriptionStatus?: RobSubscriptionStatus;
    agendaSubscriptionStatus?: Record<string, RobSubscriptionStatus>;
    robUpdatedAt?: Date;
    personAclDto: PersonAclDto;
}

export interface PersonFilterDto extends PageableDto, SortableDto {
    nameLike?: string;
    firstNameLike?: string;
    lastNameLike?: string;
    emailLike?: string;
}

export class PersonApiClient {

    constructor(private readonly axios: Axios) {
    }

    findById(personId: number): Promise<PersonDto | undefined> {
        return new Promise((resolve, reject) => {
            this.axios.get<PersonDto | undefined>(`/persons/${personId}`)
                .then((response) => resolve(response.data))
                .catch((error) => {
                    if (error?.response?.status === 404) {
                        resolve(undefined);
                    }
                    reject(error);
                });
        });
    }

    findByFilter(filter: PersonFilterDto): Promise<PageableResultListDto<PersonDto>> {
        return new Promise((resolve, reject) => {
            this.axios.post<PageableResultListDto<PersonDto>>("/persons:search", filter)
                .then((response) => resolve(response.data))
                .catch((error) => reject(error));
        });
    }

    countByFilter(filter: PersonFilterDto): Promise<number> {
        return new Promise((resolve, reject) => {
            this.axios.post<number>("/persons:count", filter)
                .then((response) => resolve(response.data))
                .catch((error) => reject(error));
        });
    }

    forceSyncWithRob(personId: number): Promise<void> {
        return new Promise((resolve, reject) => {
            this.axios.post<void>(`/persons/${personId}:force-sync-with-rob`)
                .then((response) => resolve(response.data))
                .catch((error) => reject(error));
        });
    }

    forceSyncRezaRepresentations(personId: number): Promise<void> {
        return new Promise((resolve, reject) => {
            this.axios.post<void>(`/persons/${personId}:force-sync-reza-representations`)
                .then((response) => resolve(response.data))
                .catch((error) => reject(error));
        });
    }

    subscribeToUpdatesFromRob(personId: number): Promise<void> {
        return new Promise((resolve, reject) => {
            this.axios.post<void>(`/persons/${personId}:subscribe-to-updates-from-rob`)
                .then((response) => resolve(response.data))
                .catch((error) => reject(error));
        });
    }

    unsubscribeToUpdatesFromRob(personId: number): Promise<void> {
        return new Promise((resolve, reject) => {
            this.axios.post<void>(`/persons/${personId}:unsubscribe-to-updates-from-rob`)
                .then((response) => resolve(response.data))
                .catch((error) => reject(error));
        });
    }

}
